import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useCallback, useRef, useEffect } from 'react';
import { useSpinDelay } from 'spin-delay';
import { useLocalizedStrings } from '../LocalizationProvider';
import { Spinner } from '../Spinner';
import { Select } from '../Select';
import { cn } from '../utils/tailwind';
import styles from './InlineEditableSelect.module.css';
import { Icon } from '../Icon';
export const InlineEditableSelect = ({ value, options, onSave, label, placeholder, required = false, disabled = false, error, className = "", description, labelLocation = 'above', hideLabel = false, anchor, dir, customIcon, customButtonTextRenderer }) => {
    const L = useLocalizedStrings();
    const [isLoading, setIsLoading] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    const [internalError, setInternalError] = useState(null);
    const [localValue, setLocalValue] = useState(value);
    const hideSuccessTimeoutRef = useRef(null);
    const showSpinner = useSpinDelay(isLoading, { delay: 250, minDuration: 250 });
    useEffect(() => {
        setLocalValue(value);
    }, [value]);
    const handleChange = useCallback(async (newValue) => {
        if (disabled) {
            return;
        }
        if (newValue?.value === value?.value) {
            return;
        }
        if (required && !newValue) {
            setInternalError(L.component_library.inline_editable_select.field_required);
            return;
        }
        setIsLoading(true);
        setInternalError(null);
        setLocalValue(newValue);
        try {
            await onSave?.(newValue);
            setShowSuccess(true);
            if (hideSuccessTimeoutRef.current) {
                clearTimeout(hideSuccessTimeoutRef.current);
            }
            hideSuccessTimeoutRef.current = setTimeout(() => {
                setShowSuccess(false);
                hideSuccessTimeoutRef.current = null;
            }, 2500);
        }
        catch (err) {
            setInternalError(err?.message || "Failed to save");
            setLocalValue(value);
        }
        finally {
            setIsLoading(false);
        }
    }, [value, required, onSave, L, disabled]);
    useEffect(() => {
        if (localValue !== value) {
            setInternalError(null);
        }
    }, [localValue, value]);
    useEffect(() => {
        return () => {
            if (hideSuccessTimeoutRef.current) {
                clearTimeout(hideSuccessTimeoutRef.current);
            }
        };
    }, []);
    const hasError = error?.hasError || !!internalError;
    const errorMessage = error?.errorMessage || internalError;
    return (_jsx("div", { className: "inline-flex items-start gap-2 max-w-full", children: _jsxs("div", { className: "flex-1 min-w-0", children: [_jsx("div", { className: disabled || isLoading ? "pointer-events-none" : "", onClick: disabled || isLoading ? (e) => e.preventDefault() : undefined, children: _jsxs("div", { className: "flex flex-row gap-1", children: [_jsx(Select, { value: localValue, onChange: handleChange, options: options, label: label, description: description || `Select ${label}`, labelLocation: labelLocation, hideLabel: hideLabel, placeholder: placeholder || L.component_library.inline_editable_select.select_an_option, className: cn(className, !showSuccess && !hasError && !showSpinner && !isLoading && "ring-1 ring-gray-300 ring-offset-0 focus:ring-1 focus:ring-gray-300 focus:ring-offset-0", showSuccess && !isLoading && "!ring-2 !ring-green-700 !ring-offset-0 focus:!ring-2 focus:!ring-green-700 focus:!ring-offset-0 focus-visible:!ring-2 focus-visible:!ring-green-700 focus-visible:!ring-offset-0 !bg-green-100/40", disabled && "ring-1 ring-gray-300 ring-offset-0 focus:ring-1 focus:ring-gray-300 focus:ring-offset-0", showSpinner || isLoading && `ring-1 ring-indigo-300 ring-offset-0 focus:ring-1 focus:ring-indigo-300 focus:ring-offset-0 ${styles.pulseRing}`, {
                                    "opacity-60 cursor-not-allowed": disabled || isLoading,
                                    [styles.flashAnimation]: showSuccess && !isLoading,
                                    "ring-2 ring-red-600 ring-offset-0 focus:ring-2 focus:ring-red-600 focus:ring-offset-0": hasError,
                                }), anchor: anchor, dir: dir, customButtonTextRenderer: customButtonTextRenderer }), _jsxs("div", { className: "w-6 flex h-6 justify-center items-center self-start flex-shrink-0", style: { marginTop: labelLocation === 'above' && !hideLabel ? '36px' : '8px' }, children: [showSpinner && (_jsx(Spinner, { size: "xsmall", className: "text-indigo-600" })), showSuccess && !showSpinner && (_jsxs("div", { className: cn("text-green-900", styles.fadeInAnimation), children: [_jsx(Icon, { name: "CheckCircleIcon", type: "solid", size: "md" }), _jsx("span", { className: "sr-only", "aria-live": "polite", children: L.component_library.inline_editable_select.changes_saved })] })), !showSpinner && !showSuccess && customIcon && (_jsx("div", { className: "text-gray-500", children: customIcon }))] })] }) }), hasError && errorMessage && (_jsx("div", { className: "mt-1 text-sm text-red-600", children: errorMessage }))] }) }));
};
export default InlineEditableSelect;
